import { World } from '@ecs/World';
import { FixedTimestep } from '@/core/FixedTimestep';
import { PureRenderSystem } from '@renderSystems/PureRenderSystem';
import { ParticleSystem } from '@renderSystems/ParticleSystem';
import { HealthDisplayLogicSystem } from '@renderSystems/HealthDisplayLogicSystem';
import { MovementSystem } from '@systems/MovementSystem';
import { LifetimeSystem } from '@systems/LifetimeSystem';
import { DamageSmokeSystem } from '@systems/DamageSmokeSystem';
import { DockingSystem } from '@systems/DockingSystem';
import { HangarAnimationSystem } from '@systems/HangarAnimationSystem';
import { HangarEffectSystem } from '@systems/HangarEffectSystem';
import { Position } from '@components/Position';
import { Renderable } from '@components/Renderable';
import { Docked } from '@components/Docked';
import { RosterLink } from '@components/RosterLink';
import { Welding } from '@components/Welding';
import { Assembling } from '@components/Assembling';
import { BaseLayout, BaseTarget, berthPosition, computeBaseLayout, outwardAngle, pickBaseTarget } from '@/base/BaseLayout';
import { CreditTicker } from '@/base/CreditTicker';
import { HangarController, HangarProgress } from '@/base/HangarController';
import { PLAYER_SHIP_CLASSES, PLAYER_SHIP_INFO, PlayerShipClass, SHIP_UNLOCK_LEVEL } from '@entities/PlayerShips';
import { getPlayerShipMaxHull } from '@entities/EntityFactory';
import { RosterShip } from '@/fleet/Fleet';
import { drawHullIcon } from '@/ui/HullIcon';
import { withAlpha } from '@utils/ColorUtils';

const STEP_MS = 16;
const ARRIVAL_DELAY = 36;      // frames before earned credits start rolling in
const LAUNCH_HOLD = 24;        // frames to linger after the last ship has left

/** What the player brings back to the base after a mission. */
export interface BaseArrival {
  creditsEarned: number;
  lost: string[];
}

const $ = <T extends HTMLElement = HTMLElement>(id: string): T | null => document.getElementById(id) as T | null;

const escapeHtml = (text: string): string =>
  text.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

/**
 * The mothership screen shown between missions. It runs its own small ECS world on
 * the combat canvas with the same renderer, starfield and particles, and lays HTML
 * panels over it for the shipyard, the ship inspector and the launch control.
 */
export class BaseScene {
  private readonly world = new World();
  private readonly renderer = new PureRenderSystem();
  private readonly docking: DockingSystem;
  private readonly hangar: HangarController;
  private readonly clock = new FixedTimestep(1000 / 60);
  private readonly ticker: CreditTicker;
  private layout: BaseLayout;

  private active = false;
  private frameId: number | null = null;
  private frame = 0;
  private arrival: BaseArrival | null = null;
  private arrivalCountdown = 0;
  private launchHold = 0;

  private hover: BaseTarget | null = null;
  private pressed: BaseTarget | null = null;
  private pointerTouch = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly progress: HangarProgress,
    private readonly onLaunched: () => void
  ) {
    this.layout = computeBaseLayout(canvas.width, canvas.height);
    this.docking = new DockingSystem(() => this.layout);
    this.hangar = new HangarController(this.world, progress, () => this.layout);
    this.ticker = new CreditTicker(progress.getFleet().getCredits());

    this.renderer.setContext(canvas.getContext('2d')!);
    this.renderer.setUnderlay((ctx, time) => this.drawUnderlay(ctx, time));
    this.renderer.setOverlay(ctx => this.drawOverlay(ctx));

    this.world.addSystem(this.docking);
    this.world.addSystem(new HangarAnimationSystem());
    this.world.addSystem(new HangarEffectSystem());
    this.world.addSystem(new DamageSmokeSystem());
    this.world.addSystem(new MovementSystem());
    this.world.addSystem(new HealthDisplayLogicSystem());
    this.world.addSystem(new LifetimeSystem());
    this.world.addSystem(new ParticleSystem());
    this.world.addSystem(this.renderer);

    canvas.addEventListener('pointerdown', e => this.onPointerDown(e));
    canvas.addEventListener('pointermove', e => this.onPointerMove(e));
    canvas.addEventListener('pointerup', e => this.onPointerUp(e));
    canvas.addEventListener('pointerleave', () => { this.hover = null; this.pressed = null; this.updateCursor(); });
    canvas.addEventListener('pointercancel', () => { this.pressed = null; });
    this.bindPanels();
  }

  isActive(): boolean {
    return this.active;
  }

  show(arrival: BaseArrival | null = null): void {
    this.active = true;
    this.arrival = arrival;
    this.launchHold = 0;
    this.hover = this.pressed = null;
    document.body.classList.add('at-base');
    this.setDisplay('baseScreen', 'block');

    this.layout = computeBaseLayout(this.canvas.width, this.canvas.height);
    this.hangar.populate();

    const credits = this.progress.getFleet().getCredits();
    if (arrival && arrival.creditsEarned > 0) {
      this.ticker.set(credits - arrival.creditsEarned);
      this.arrivalCountdown = ARRIVAL_DELAY;
    } else {
      this.ticker.set(credits);
    }
    this.showArrivalNotes(arrival);
    this.renderPanels();
    this.updateCredits(this.ticker.step().value);

    this.clock.reset(performance.now());
    this.scheduleFrame();
  }

  hide(): void {
    this.active = false;
    if (this.frameId !== null) cancelAnimationFrame(this.frameId);
    this.frameId = null;
    document.body.classList.remove('at-base');
    this.setDisplay('baseScreen', 'none');
    this.canvas.style.cursor = '';
  }

  resize(): void {
    this.layout = computeBaseLayout(this.canvas.width, this.canvas.height);
    if (!this.active) return;
    // Re-dock at the new scale; ships mid-launch are not worth preserving through a resize
    if (!this.hangar.isLaunching()) this.hangar.populate();
  }

  // ---------------------------------------------------------------------------
  // Loop

  private scheduleFrame(): void {
    this.frameId = requestAnimationFrame(now => this.tick(now));
  }

  private tick(now: number): void {
    this.frameId = null;
    if (!this.active) return;

    const steps = this.clock.advance(now);
    for (let i = 0; i < steps && this.active; i++) this.step();

    if (this.active) this.scheduleFrame();
  }

  private step(): void {
    this.frame++;
    this.world.update(STEP_MS);

    if (this.arrivalCountdown > 0 && --this.arrivalCountdown === 0 && this.arrival) {
      const credits = this.progress.getFleet().getCredits();
      this.ticker.start(credits - this.arrival.creditsEarned, credits, 100);
      $('baseCredits')?.classList.add('earning');
      this.showCreditGain(this.arrival.creditsEarned);
    }

    const { value, ticked } = this.ticker.step();
    this.updateCredits(value);
    if (ticked) {
      const at = this.creditsAnchor();
      this.hangar.requestEffect('credits', at.x, at.y);
    }
    if (!this.ticker.isRunning()) $('baseCredits')?.classList.remove('earning');

    if (this.hangar.isLaunchComplete() && ++this.launchHold >= LAUNCH_HOLD) {
      this.hide();
      this.onLaunched();
    }

    // Refresh live values (repair progress, launch state) a few times a second
    if (this.frame % 15 === 0) this.renderFooter();
  }

  // ---------------------------------------------------------------------------
  // Input

  private pointerPos(e: PointerEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * this.canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * this.canvas.height,
    };
  }

  private pick(e: PointerEvent): BaseTarget | null {
    if (this.hangar.isLaunching()) return null;
    const { x, y } = this.pointerPos(e);
    const ships = this.hangar.getShipEntities()
      .filter(ship => ship.getComponent(Docked)!.phase === 'docked')
      .map(ship => ({
        id: ship.getComponent(RosterLink)!.shipId,
        berth: ship.getComponent(Docked)!.berth,
        ...ship.getComponent(Position)!,
      }));
    return pickBaseTarget(this.layout, ships, x, y, e.pointerType === 'touch');
  }

  private onPointerDown(e: PointerEvent): void {
    if (!this.active) return;
    e.preventDefault();
    this.pointerTouch = e.pointerType === 'touch';
    this.pressed = this.pick(e);
    this.hover = this.pressed;
    this.updateCursor();
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.active || e.pointerType === 'touch') return;
    this.hover = this.pick(e);
    this.updateCursor();
  }

  private onPointerUp(e: PointerEvent): void {
    if (!this.active) return;
    const target = this.pick(e);
    const pressed = this.pressed;
    this.pressed = null;
    if (this.pointerTouch) this.hover = null;

    if (!target || !pressed || !sameTarget(target, pressed)) {
      if (!target && !pressed) this.selectShip(null);
      return;
    }

    if (target.kind === 'ship') {
      // A second click on the inspected ship toggles it into or out of the deployment
      if (this.hangar.getSelectedId() === target.id) this.toggleDeploy(target.id);
      else this.selectShip(target.id);
    } else {
      this.toast('Empty berth: build a ship at the shipyard to fill it');
      const yard = $('baseShipyard');
      yard?.classList.remove('attention');
      void yard?.offsetWidth; // restart the animation
      yard?.classList.add('attention');
    }
  }

  private updateCursor(): void {
    this.canvas.style.cursor = this.hover ? 'pointer' : 'default';
  }

  // ---------------------------------------------------------------------------
  // Actions

  private selectShip(id: number | null): void {
    this.hangar.select(id);
    this.renderInspector();
  }

  private build(shipClass: PlayerShipClass): void {
    const result = this.hangar.build(shipClass);
    if (result.ok) {
      this.toast(`${result.ship.name} commissioned${result.cost === 0 ? ' at no cost' : ''}`);
    } else {
      this.toast({
        locked: `${PLAYER_SHIP_INFO[shipClass].name} unlocks at level ${SHIP_UNLOCK_LEVEL[shipClass]}`,
        credits: 'Not enough credits',
        hangarFull: 'Every berth is taken',
      }[result.reason], true);
    }
    this.afterAction();
  }

  private repair(id: number): void {
    const ship = this.progress.getFleet().getShip(id);
    const cost = this.progress.getFleet().repairCost(id);
    if (this.hangar.repair(id)) this.toast(`${ship?.name} repaired for ${cost} credits`);
    else this.toast(cost === 0 ? 'Hull is already intact' : 'Not enough credits for repairs', true);
    this.afterAction();
  }

  private toggleDeploy(id: number): void {
    const result = this.hangar.toggleDeploy(id);
    if (result === 'full') this.toast(`Deployment full: this mission takes ${this.hangar.getDeploymentLimit()}`, true);
    this.afterAction();
  }

  private launch(): void {
    if (!this.hangar.launch()) {
      this.toast('Deploy at least one ship first', true);
      return;
    }
    this.hover = this.pressed = null;
    this.updateCursor();
    this.afterAction();
  }

  private afterAction(): void {
    if (!this.ticker.isRunning()) this.ticker.set(this.progress.getFleet().getCredits());
    this.renderPanels();
  }

  // ---------------------------------------------------------------------------
  // Canvas layers

  private drawUnderlay(ctx: CanvasRenderingContext2D, time: number): void {
    const sway = this.docking.getSway();
    const fleet = this.progress.getFleet();
    const radius = this.layout.berthRadius;

    for (const berth of fleet.getBerths()) {
      const p = berthPosition(this.layout, berth.berth, sway);
      const outward = outwardAngle(this.layout, berth.berth);
      const hovered = this.hover?.kind === 'berth' && this.hover.berth === berth.berth;
      const pressed = this.pressed?.kind === 'berth' && this.pressed.berth === berth.berth;
      const lost = !berth.ship && berth.lostName;
      const tint = lost ? '#ff6b6b' : berth.ship ? '#5cc8ff' : '#8fa3bf';

      // Docking arm back to the hull, with a pulse running along it
      const armLength = this.layout.size * 0.28;
      const hx = p.x - Math.cos(outward) * (radius * 0.6 + armLength);
      const hy = p.y - Math.sin(outward) * (radius * 0.6 + armLength);
      ctx.strokeStyle = withAlpha('#9fb4d1', 0.35);
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(hx, hy);
      ctx.lineTo(p.x - Math.cos(outward) * radius * 0.6, p.y - Math.sin(outward) * radius * 0.6);
      ctx.stroke();
      const pulse = ((time / 900 + berth.berth * 0.17) % 1);
      ctx.fillStyle = withAlpha(tint, 0.8 * (1 - pulse));
      ctx.beginPath();
      ctx.arc(hx + (p.x - hx) * pulse, hy + (p.y - hy) * pulse, 1.8, 0, Math.PI * 2);
      ctx.fill();

      // Landing pad
      const pad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
      pad.addColorStop(0, withAlpha(tint, hovered ? 0.2 : 0.1));
      pad.addColorStop(1, withAlpha(tint, 0));
      ctx.fillStyle = pad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius * (pressed ? 0.92 : 1), 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = withAlpha(tint, hovered ? 0.9 : berth.ship ? 0.35 : 0.5);
      ctx.lineWidth = hovered ? 2 : 1;
      if (!berth.ship) {
        ctx.setLineDash([5, 5]);
        ctx.lineDashOffset = -time * 0.01;
      }
      ctx.stroke();
      ctx.setLineDash([]);

      // Clamp marks
      for (let i = 0; i < 4; i++) {
        const a = this.layout.angle + Math.PI / 4 + (Math.PI / 2) * i;
        ctx.fillStyle = withAlpha(tint, 0.6);
        ctx.fillRect(p.x + Math.cos(a) * radius - 1.5, p.y + Math.sin(a) * radius - 1.5, 3, 3);
      }

      if (!berth.ship) {
        ctx.font = '600 10px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = withAlpha(tint, 0.85);
        if (lost) {
          ctx.fillText('LOST', p.x, p.y - 6);
          ctx.fillStyle = withAlpha('#ffb3b3', 0.7);
          ctx.fillText(berth.lostName!, p.x, p.y + 7);
        } else {
          ctx.fillText('EMPTY', p.x, p.y);
        }
      }
    }
  }

  private drawOverlay(ctx: CanvasRenderingContext2D): void {
    const fleet = this.progress.getFleet();
    const pulse = 0.5 + 0.5 * Math.sin(this.frame * 0.12);

    for (const entity of this.hangar.getShipEntities()) {
      const docked = entity.getComponent(Docked)!;
      if (docked.phase === 'gone' || (docked.phase !== 'docked' && docked.delay === 0)) continue;

      const ship = fleet.getShip(entity.getComponent(RosterLink)!.shipId);
      if (!ship) continue;
      const position = entity.getComponent(Position)!;
      const renderable = entity.getComponent(Renderable)!;
      const target: BaseTarget = { kind: 'ship', id: ship.id };
      const hovered = !!this.hover && sameTarget(this.hover, target);
      const pressed = !!this.pressed && sameTarget(this.pressed, target);

      if (hovered || pressed) {
        ctx.save();
        ctx.strokeStyle = withAlpha('#ffffff', pressed ? 0.9 : 0.35 + 0.35 * pulse);
        ctx.fillStyle = withAlpha('#5cc8ff', pressed ? 0.18 : 0.06);
        ctx.lineWidth = pressed ? 2.5 : 1.5;
        ctx.beginPath();
        ctx.arc(position.x, position.y, this.layout.berthRadius * (pressed ? 0.9 : 1.05), 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }

      this.drawLabel(ctx, ship, position, renderable, fleet.isDeployed(ship.id), entity.hasComponent(Welding), entity.hasComponent(Assembling));
    }
  }

  private drawLabel(
    ctx: CanvasRenderingContext2D, ship: RosterShip, position: { x: number; y: number }, renderable: Renderable,
    deployed: boolean, welding: boolean, assembling: boolean
  ): void {
    const compact = this.layout.size < 200;
    const lineHeight = compact ? 11 : 13;
    const damaged = ship.hull < ship.maxHull;
    const status = assembling ? 'ASSEMBLING' : welding ? 'REPAIRING' : deployed ? 'DEPLOYED' : 'RESERVE';

    const statusColor = deployed ? '#50ffa0' : damaged ? '#fbbf24' : '#7f8ea6';
    // Small screens get a two-line label; the hull shape and the inspector still give the class
    const lines: { text: string; font: string; color: string }[] = compact
      ? [
        { text: ship.name, font: '700 11px Inter, system-ui, sans-serif', color: '#eef3fb' },
        { text: `${ship.hull}/${ship.maxHull} · ${ship.missions} MSN`, font: '600 9px Inter, system-ui, sans-serif', color: statusColor },
      ]
      : [
        { text: ship.name, font: '700 12px Inter, system-ui, sans-serif', color: '#eef3fb' },
        { text: `${PLAYER_SHIP_INFO[ship.shipClass].name} · ${ship.missions} mission${ship.missions === 1 ? '' : 's'}`, font: '500 10px Inter, system-ui, sans-serif', color: '#b3c1d6' },
        { text: `HULL ${ship.hull}/${ship.maxHull} · ${status}`, font: '600 10px Inter, system-ui, sans-serif', color: statusColor },
      ];

    // Labels sit on the outer side of the berth, or under the ship on nose-up layouts
    const outward = this.layout.labelsBelow ? Math.PI / 2 : outwardAngle(this.layout, ship.berth);
    const dx = Math.cos(outward);
    const dy = Math.sin(outward);
    // Labels above a ship clear its health bar, which is always drawn above
    const clearance = dy < -0.5 ? renderable.size * 1.7 + 10 : 0;
    const distance = Math.max(this.layout.labelsBelow ? renderable.size * 1.1 : this.layout.berthRadius, clearance) + (compact ? 6 : 10);
    const alpha = renderable.alpha;
    const x = position.x + dx * distance;
    const y = position.y + dy * distance;
    const upward = dy < -0.5;
    const align: CanvasTextAlign = Math.abs(dx) > 0.5 ? (dx > 0 ? 'left' : 'right') : 'center';

    ctx.save();
    ctx.globalAlpha = Math.max(0.25, alpha);
    ctx.textAlign = align;
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
    ctx.shadowBlur = 4;
    const ordered = upward ? [...lines].reverse() : lines;
    const startY = align === 'center' ? y + (upward ? -lineHeight / 2 : lineHeight / 2) : y - lineHeight;
    ordered.forEach((line, i) => {
      ctx.font = line.font;
      ctx.fillStyle = line.color;
      const offset = align === 'center' ? (upward ? -i : i) * lineHeight : i * lineHeight;
      ctx.fillText(line.text, x, startY + offset);
    });
    ctx.restore();
  }

  // ---------------------------------------------------------------------------
  // HTML panels

  private bindPanels(): void {
    $('baseLaunchBtn')?.addEventListener('click', () => this.launch());
    $('baseShipyardList')?.addEventListener('click', e => {
      // Unavailable classes stay clickable so a refused build still gets its denied feedback
      const button = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-build]');
      if (button) this.build(button.dataset.build as PlayerShipClass);
    });
    $('baseInspector')?.addEventListener('click', e => {
      const button = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-action]');
      const id = this.hangar.getSelectedId();
      if (!button || id === null) return;
      if (button.dataset.action === 'deploy') this.toggleDeploy(id);
      if (button.dataset.action === 'repair') this.repair(id);
      if (button.dataset.action === 'close') this.selectShip(null);
    });
  }

  private renderPanels(): void {
    this.renderHeader();
    this.renderShipyard();
    this.renderInspector();
    this.renderFooter();
  }

  private renderHeader(): void {
    const level = this.hangar.getTargetLevel();
    const mission = $('baseMission');
    if (mission) {
      mission.innerHTML = `Next mission <strong>Level ${level.id} · ${escapeHtml(level.name)}</strong> · deploy up to ${level.deploymentLimit}`;
    }
  }

  private renderShipyard(): void {
    const list = $('baseShipyardList');
    if (!list) return;
    const fleet = this.progress.getFleet();
    const level = this.progress.getHighestUnlockedLevel();

    list.innerHTML = PLAYER_SHIP_CLASSES.map(cls => {
      const info = PLAYER_SHIP_INFO[cls];
      const check = fleet.canBuild(cls, level);
      const cost = fleet.buildCost(cls);
      const locked = !check.ok && check.reason === 'locked';
      const state = check.ok ? '' : ` ${check.reason}`;
      const price = locked ? `Level ${SHIP_UNLOCK_LEVEL[cls]}` : cost === 0 ? 'FREE' : `◆ ${cost}`;
      return `
        <button class="yard-item${state}" data-build="${cls}" style="--ship-color: ${info.color}" aria-disabled="${!check.ok}"
          title="${escapeHtml(info.description)}">
          <canvas class="yard-icon" width="44" height="44" data-icon="${cls}"></canvas>
          <span class="yard-text">
            <span class="yard-name">${info.name}</span>
            <span class="yard-desc">${locked ? 'Locked' : `Hull ${getPlayerShipMaxHull(cls)} · ${escapeHtml(info.description)}`}</span>
          </span>
          <span class="yard-price">${price}</span>
        </button>`;
    }).join('');

    list.querySelectorAll<HTMLCanvasElement>('canvas[data-icon]').forEach(icon => {
      const cls = icon.dataset.icon as PlayerShipClass;
      drawHullIcon(icon, cls, PLAYER_SHIP_INFO[cls].color);
    });
  }

  private renderInspector(): void {
    const panel = $('baseInspectorBody');
    if (!panel) return;
    const fleet = this.progress.getFleet();
    const ship = this.hangar.getSelectedShip();

    if (!ship) {
      const damaged = fleet.getShips().filter(s => s.hull < s.maxHull).length;
      panel.innerHTML = `
        <p class="inspector-hint">Select a docked ship to inspect, repair or deploy it. Click it again to toggle deployment.</p>
        <dl class="stat-grid">
          <dt>Ships in roster</dt><dd>${fleet.getShips().length}</dd>
          <dt>Deployed</dt><dd>${fleet.getDeployed().length}</dd>
          <dt>Need repairs</dt><dd>${damaged}</dd>
        </dl>`;
      return;
    }

    const info = PLAYER_SHIP_INFO[ship.shipClass];
    const deployed = fleet.isDeployed(ship.id);
    const repairCost = fleet.repairCost(ship.id);
    const pips = Array.from({ length: ship.maxHull }, (_, i) => `<span class="pip${i < ship.hull ? ' full' : ''}"></span>`).join('');
    panel.innerHTML = `
      <div class="inspector-head" style="--ship-color: ${info.color}">
        <canvas class="yard-icon" width="52" height="52" id="inspectorIcon"></canvas>
        <div>
          <div class="inspector-name">${escapeHtml(ship.name)}</div>
          <div class="inspector-class">${info.name}<span class="inspector-desc"> · ${escapeHtml(info.description)}</span></div>
        </div>
        <button class="icon-btn inspector-close" data-action="close" aria-label="Close">✕</button>
      </div>
      <div class="inspector-hull"><span>Hull ${ship.hull}/${ship.maxHull}</span><div class="fleet-card-hp">${pips}</div></div>
      <dl class="stat-grid">
        <dt>Missions survived</dt><dd>${ship.missions}</dd>
        <dt>Status</dt><dd class="${deployed ? 'status-deployed' : ''}">${deployed ? 'Deployed' : 'In reserve'}</dd>
      </dl>
      <div class="inspector-actions">
        <button class="btn ${deployed ? 'btn-secondary' : ''}" data-action="deploy">${deployed ? 'Stand down' : 'Deploy'}</button>
        <button class="btn btn-repair" data-action="repair" ${repairCost === 0 ? 'disabled' : ''}>
          ${repairCost === 0 ? 'Hull intact' : `Repair · ◆ ${repairCost}`}
        </button>
      </div>`;

    const icon = $<HTMLCanvasElement>('inspectorIcon');
    if (icon) drawHullIcon(icon, ship.shipClass, info.color);
  }

  private renderFooter(): void {
    const fleet = this.progress.getFleet();
    const limit = this.hangar.getDeploymentLimit();
    const deployed = fleet.getDeployed();
    const count = $('baseDeployCount');
    if (count) {
      const slots = Array.from({ length: limit }, (_, i) => {
        const ship = deployed[i];
        return `<span class="deploy-slot${ship ? ' filled' : ''}" style="--ship-color: ${ship ? PLAYER_SHIP_INFO[ship.shipClass].color : 'transparent'}"
          title="${ship ? escapeHtml(ship.name) : 'Empty slot'}"></span>`;
      }).join('');
      count.innerHTML = `<span class="section-label">Squadron ${deployed.length}/${limit}</span><span class="deploy-slots">${slots}</span>`;
    }

    const launch = $<HTMLButtonElement>('baseLaunchBtn');
    if (launch) {
      const launching = this.hangar.isLaunching() || this.hangar.isLaunchComplete();
      launch.disabled = launching;
      launch.classList.toggle('ready', deployed.length > 0 && !launching);
      launch.textContent = launching ? 'Launching…' : 'Launch mission';
    }
  }

  private updateCredits(value: number): void {
    const element = $('baseCreditsValue');
    if (element) element.textContent = value.toLocaleString('en-GB');
  }

  private showCreditGain(amount: number): void {
    const gain = $('baseCreditsGain');
    if (!gain) return;
    gain.textContent = `+${amount.toLocaleString('en-GB')}`;
    gain.classList.remove('show');
    void gain.offsetWidth;
    gain.classList.add('show');
  }

  private showArrivalNotes(arrival: BaseArrival | null): void {
    if (!arrival) return;
    if (arrival.lost.length > 0) {
      this.toast(`Lost in action: ${arrival.lost.join(', ')}. Their berths stand empty.`, true, 5000);
    } else if (arrival.creditsEarned > 0) {
      this.toast('The squadron is home. All ships accounted for.', false, 3500);
    }
  }

  /** Canvas position of the credits counter, where earned-credit sparks appear. */
  private creditsAnchor(): { x: number; y: number } {
    const element = $('baseCredits');
    if (!element) return { x: this.canvas.width / 2, y: 40 };
    const rect = element.getBoundingClientRect();
    const canvasRect = this.canvas.getBoundingClientRect();
    const scaleX = this.canvas.width / canvasRect.width;
    const scaleY = this.canvas.height / canvasRect.height;
    return {
      x: (rect.left + rect.width * (0.2 + Math.random() * 0.6) - canvasRect.left) * scaleX,
      y: (rect.bottom - canvasRect.top) * scaleY,
    };
  }

  private toastTimer: number | null = null;

  private toast(message: string, warning: boolean = false, duration: number = 2600): void {
    const element = $('baseToast');
    if (!element) return;
    element.textContent = message;
    element.classList.toggle('warning', warning);
    element.classList.remove('show');
    void element.offsetWidth;
    element.classList.add('show');
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => element.classList.remove('show'), duration);
  }

  private setDisplay(id: string, display: string): void {
    const element = $(id);
    if (element) element.style.display = display;
  }
}

function sameTarget(a: BaseTarget, b: BaseTarget): boolean {
  return a.kind === 'ship' ? b.kind === 'ship' && a.id === b.id : b.kind === 'berth' && a.berth === b.berth;
}
